import React, { useEffect, useState } from "react";
import {
  Search,
  ArrowUp,
  ArrowDown,
  ChevronLeft,
  ChevronRight,
  TrendingUp,
  AlertTriangle,
  Loader,
  Eye,
  Users,
  Grid,
  Filter,
  BellOff, // Add this import
} from "lucide-react";
import PortfolioDetailsPage from "./PortfolioDetailsModal";
import adminPortfolioService from "@/services/api/admin.portfolio.service";

import { notificationService } from "../../../services/api/notification.service";
import FinancialReportsPanel from "./FinancialReportsPanel";
import FinancialReportsPage from "./FinancialReportsPage";

// Helper function to check if portfolio is new (created within last 24-48 hours)
const isNewPortfolio = (createdAt, markedAsReadPortfolios = new Set()) => {
  if (!createdAt) return false;

  const portfolioDate = new Date(createdAt);
  const currentDate = new Date();
  const timeDifferenceHours = (currentDate - portfolioDate) / (1000 * 60 * 60);

  // Consider portfolio as "new" if created within last 48 hours and not marked as read
  return timeDifferenceHours <= 48 && !markedAsReadPortfolios.has(createdAt);
};

// Helper functions to manage localStorage for marked portfolios
const getMarkedPortfoliosFromStorage = () => {
  try {
    const stored = localStorage.getItem('markedAsReadPortfolios');
    if (stored) {
      const parsedData = JSON.parse(stored);
      // Convert array back to Set and filter out old entries (older than 7 days)
      const currentTime = new Date().getTime();
      const validEntries = parsedData.filter(entry => {
        const entryTime = new Date(entry).getTime();
        const daysDifference = (currentTime - entryTime) / (1000 * 60 * 60 * 24);
        return daysDifference <= 7; // Keep entries for 7 days
      });
      return new Set(validEntries);
    }
  } catch (error) {
    console.error('Error reading marked portfolios from localStorage:', error);
  }
  return new Set();
};

const saveMarkedPortfoliosToStorage = (markedPortfolios) => {
  try {
    // Convert Set to Array for storage
    const portfoliosArray = Array.from(markedPortfolios);
    localStorage.setItem('markedAsReadPortfolios', JSON.stringify(portfoliosArray));
  } catch (error) {
    console.error('Error saving marked portfolios to localStorage:', error);
  }
};

// Replace the fetchPortfolios function with:
const fetchPortfolios = async (params = {}) => {
  try {
    const result = await adminPortfolioService.getPortfolios(params);

    if (result.success) {
      // Check which view mode we're in
      const isUserView = params.groupByUser === "true";

      if (isUserView) {
        return {
          data: result.data.usersWithPortfolios || [],
          pagination: result.data.pagination || {},
        };
      } else {
        return {
          data: result.data.portfolios || [],
          pagination: result.data.pagination || {},
        };
      }
    } else {
      console.error("API returned error:", result);
      return { data: [], pagination: {} };
    }
  } catch (error) {
    console.error("Error fetching portfolios:", error);
    return { data: [], pagination: {} };
  }
};

export default function UserPortfolios() {
  // State variables
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("portfolioName");
  const [sortOrder, setSortOrder] = useState("asc");
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    pages: 1,
    limit: 10,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPortfolioId, setSelectedPortfolioId] = useState(null);
  const [viewMode, setViewMode] = useState("portfolios");
  const [filters, setFilters] = useState({
    portfolioType: "",
    status: "",
    riskProfile: "",
    investmentStrategy: "",
    minValue: "",
    maxValue: "",
  });
  const [showFilters, setShowFilters] = useState(false);

  // Initialize markedAsReadPortfolios from localStorage
  const [markedAsReadPortfolios, setMarkedAsReadPortfolios] = useState(() => {
    return getMarkedPortfoliosFromStorage();
  });
  const [markAsReadLoading, setMarkAsReadLoading] = useState(false);

  // Save to localStorage whenever markedAsReadPortfolios changes
  useEffect(() => {
    saveMarkedPortfoliosToStorage(markedAsReadPortfolios);
  }, [markedAsReadPortfolios]);

  // Load portfolios when component mounts or filters/pagination/sort changes
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const params = {
          page: currentPage,
          limit: pagination.limit,
          sortBy: sortField,
          sortOrder,
          groupByUser: viewMode === "users" ? "true" : "false",
          ...filters,
        };

        const result = await fetchPortfolios(params);
        setData(result.data);
        setPagination(result.pagination);
        setError(null);
      } catch (err) {
        setError(
          "Failed to load data. Please check your connection and try again."
        );
        console.error("Data loading error:", err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [currentPage, sortField, sortOrder, viewMode, filters]);

  // Add function to handle mark as read
  const handleMarkAsRead = async () => {
    // Get count of new portfolios
    const newPortfoliosCount = data.filter(portfolio =>
      isNewPortfolio(portfolio.createdAt, markedAsReadPortfolios)
    ).length;

    if (newPortfoliosCount === 0) return;

    setMarkAsReadLoading(true);
    try {
      // Call the notification service to mark Portfolio category as read
      await notificationService.markCategoryAsRead("Portfolio");

      // Mark all current new portfolios as read locally
      const newMarkedAsRead = new Set(markedAsReadPortfolios);
      data.forEach(portfolio => {
        if (isNewPortfolio(portfolio.createdAt, markedAsReadPortfolios)) {
          newMarkedAsRead.add(portfolio.createdAt);
        }
      });

      setMarkedAsReadPortfolios(newMarkedAsRead);

    } catch (error) {
      console.error("Failed to mark Portfolio notifications as read:", error);
      // You might want to show a toast notification here
    } finally {
      setMarkAsReadLoading(false);
    }
  };

  // Function to get count of new portfolios
  const getNewPortfoliosCount = () => {
    return data.filter(portfolio =>
      isNewPortfolio(portfolio.createdAt, markedAsReadPortfolios)
    ).length;
  };

  // Handle sort toggle
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortOrder("asc");
    }
    setCurrentPage(1); // Reset to first page on sort change
  };

  // Sort icon helper
  const sortIcon = (field) => {
    if (sortField !== field) return null;
    return sortOrder === "asc" ? (
      <ArrowUp className="w-4 h-4 ml-1" />
    ) : (
      <ArrowDown className="w-4 h-4 ml-1" />
    );
  };

  // Function to handle view details button click
  const handleViewDetails = (portfolioId) => {
    setSelectedPortfolioId(portfolioId);
  };

  // Function to go back to dashboard view
  const handleBackToDashboard = () => {
    setSelectedPortfolioId(null);
  };

  // Function to format date
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Get profit status color class
  const getProfitColorClass = (profit) => {
    if (!profit && profit !== 0) return "text-gray-600";
    if (profit > 0) return "text-green-600";
    if (profit < 0) return "text-red-600";
    return "text-gray-600";
  };

  // Get profit icon
  const getProfitIcon = (profit) => {
    if (!profit && profit !== 0) return null;
    if (profit > 0)
      return <TrendingUp className="ml-1 w-4 h-4 text-green-600" />;
    if (profit < 0)
      return (
        <TrendingUp className="ml-1 w-4 h-4 text-red-600 transform rotate-180" />
      );
    return null;
  };

  // Get status badge class
  const getStatusBadgeClass = (status) => {
    if (!status) return "bg-gray-100 text-gray-800";
    if (status === "ACTIVE") return "bg-green-100 text-green-800";
    if (status === "INACTIVE") return "bg-yellow-100 text-yellow-800";
    return "bg-red-100 text-red-800";
  };

  // Handle search
  const handleSearch = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1); // Reset to first page on search
  };

  // Handle filter change
  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({
      ...prev,
      [name]: value,
    }));
    setCurrentPage(1); // Reset to first page on filter change
  };

  // Apply filters
  const applyFilters = (e) => {
    e.preventDefault();
    // Filters are already in state, just reload data
    // The useEffect will handle the data reload
  };

  // Reset filters
  const resetFilters = () => {
    setFilters({
      portfolioType: "",
      status: "",
      riskProfile: "",
      investmentStrategy: "",
      minValue: "",
      maxValue: "",
    });
    setCurrentPage(1);
  };

  // Toggle view mode
  const toggleViewMode = (mode) => {
    if (viewMode !== mode) {
      // Clear existing data to prevent errors while loading
      setData([]);
      setViewMode(mode);
      setCurrentPage(1); // Reset to first page on view change
    }
  };

  // If a portfolio is selected, show the details page
  if (selectedPortfolioId) {
    return (
      <PortfolioDetailsPage
        portfolioId={selectedPortfolioId}
        onBackClick={handleBackToDashboard}
      />
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader className="animate-spin text-indigo-500 mb-4" size={36} />
          <p className="mt-4 text-gray-600">Loading data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-red-600 flex flex-col items-center">
        <AlertTriangle className="h-12 w-12 mb-4" />
        <h3 className="text-lg font-medium mb-2">Error Loading Data</h3>
        <p>{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-4 bg-red-100 hover:bg-red-200 text-red-700 font-medium py-2 px-4 rounded-lg transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="p-4">
      <div className="mb-4 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-800">
            Portfolio Dashboard
          </h2>
          <p className="text-sm text-gray-500">
            Investment portfolio management
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
          <div className="relative w-full ">
            {/* <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={handleSearch}
              className="w-full py-2 pl-10 pr-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
            />
            <Search className="absolute top-2.5 left-3 h-5 w-5 text-gray-400" /> */}
          </div>

          <div className="flex gap-1">
            {/* Add Mark as Read Button - only show when there are new portfolios */}
            {getNewPortfoliosCount() > 0 && (
              <button
                onClick={handleMarkAsRead}
                disabled={markAsReadLoading}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${markAsReadLoading
                  ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                  : "bg-green-600 hover:bg-green-700 text-white shadow-md hover:shadow-lg"
                  }`}
              >
                {markAsReadLoading ? (
                  <>
                    <Loader className="h-4 w-4 animate-spin" />
                    <span className="hidden sm:inline">Marking...</span>
                  </>
                ) : (
                  <>
                    <BellOff className="h-4 w-4" />
                    <span className="hidden sm:inline">Mark All as Read</span>
                    <span className="sm:hidden">Read</span>
                    {getNewPortfoliosCount() > 0 && (
                      <span className="bg-white text-green-600 rounded-full px-2 py-1 text-xs font-semibold">
                        {getNewPortfoliosCount()}
                      </span>
                    )}
                  </>
                )}
              </button>
            )}

            <button
              onClick={() => setShowFilters(!showFilters)}
              className="bg-white border border-gray-300 rounded-lg px-3 py-2 flex items-center gap-1 hover:bg-gray-50"
            >
              <Filter className="h-5 w-5 text-gray-500" />
              <span className="hidden sm:inline">Filters</span>
            </button>

            <div className="bg-white border border-gray-300 rounded-lg flex overflow-hidden">
              <button
                onClick={() => toggleViewMode("portfolios")}
                className={`px-3 py-2 flex items-center gap-1 ${viewMode === "portfolios"
                  ? "bg-blue-50 text-blue-600"
                  : "hover:bg-gray-50"
                  }`}
              >
                <Grid className="h-5 w-5" />
                <span className="hidden sm:inline">Portfolios</span>
              </button>
              <button
                onClick={() => toggleViewMode("users")}
                className={`px-3 py-2 flex items-center gap-1 ${viewMode === "users"
                  ? "bg-blue-50 text-blue-600"
                  : "hover:bg-gray-50"
                  }`}
              >
                <Users className="h-5 w-5" />
                <span className="hidden sm:inline">Users</span>
              </button>
              <button
                onClick={() => toggleViewMode("Portfolio Report")}
                className={`px-3 py-2 flex items-center gap-1 ${viewMode === "Portfolio Report"
                  ? "bg-blue-50 text-blue-600"
                  : "hover:bg-gray-50"
                  }`}
              >
                <Users className="h-5 w-5" />
                <span className="hidden sm:inline">Portfolio Report</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <div className="mb-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <form
            onSubmit={applyFilters}
            className="grid grid-cols-1 md:grid-cols-3 gap-4"
          >
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Portfolio Type
              </label>
              <select
                name="portfolioType"
                value={filters.portfolioType}
                onChange={handleFilterChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Types</option>
                <option value="UNLISTED">Unlisted</option>
                <option value="PRE_IPO">Pre-IPO</option>
                <option value="MIXED">Mixed</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Statuses</option>
                <option value="ACTIVE">Active</option>
                <option value="INACTIVE">Inactive</option>
                <option value="CLOSED">Closed</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Risk Profile
              </label>
              <select
                name="riskProfile"
                value={filters.riskProfile}
                onChange={handleFilterChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Risk Profiles</option>
                <option value="CONSERVATIVE">Conservative</option>
                <option value="MODERATE">Moderate</option>
                <option value="AGGRESSIVE">Aggressive</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Investment Strategy
              </label>
              <select
                name="investmentStrategy"
                value={filters.investmentStrategy}
                onChange={handleFilterChange}
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">All Strategies</option>
                <option value="LONG_TERM">Long Term</option>
                <option value="SHORT_TERM">Short Term</option>
                <option value="VALUE_INVESTING">Value Investing</option>
                <option value="GROWTH">Growth</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Min Value (₹)
              </label>
              <input
                type="number"
                name="minValue"
                value={filters.minValue}
                onChange={handleFilterChange}
                placeholder="Min value"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Max Value (₹)
              </label>
              <input
                type="number"
                name="maxValue"
                value={filters.maxValue}
                onChange={handleFilterChange}
                placeholder="Max value"
                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="md:col-span-3 flex justify-end gap-2">
              <button
                type="button"
                onClick={resetFilters}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Reset
              </button>
              {/* <button
                type="submit"
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Apply Filters
              </button> */}
            </div>
          </form>
        </div>
      )}

      {/* Portfolio View */}
      {viewMode === "portfolios" && (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  onClick={() => handleSort("portfolioName")}
                  className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                >
                  <div className="flex items-center">
                    Portfolio Name {sortIcon("portfolioName")}
                  </div>
                </th>
                <th
                  onClick={() => handleSort("userId")}
                  className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                >
                  <div className="flex items-center">
                    User {sortIcon("userId")}
                  </div>
                </th>
                <th
                  onClick={() => handleSort("portfolioType")}
                  className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hidden md:table-cell"
                >
                  <div className="flex items-center">
                    Type {sortIcon("portfolioType")}
                  </div>
                </th>
                <th
                  onClick={() => handleSort("riskProfile")}
                  className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hidden md:table-cell"
                >
                  <div className="flex items-center">
                    Risk {sortIcon("riskProfile")}
                  </div>
                </th>
                <th
                  onClick={() => handleSort("status")}
                  className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                >
                  <div className="flex items-center">
                    Status {sortIcon("status")}
                  </div>
                </th>
                <th
                  onClick={() => handleSort("summary.totalInvestment")}
                  className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hidden md:table-cell"
                >
                  <div className="flex items-center">
                    Investment {sortIcon("summary.totalInvestment")}
                  </div>
                </th>
                <th
                  onClick={() => handleSort("summary.totalProfit")}
                  className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                >
                  <div className="flex items-center">
                    Profit {sortIcon("summary.totalProfit")}
                  </div>
                </th>
                <th
                  onClick={() => handleSort("createdAt")}
                  className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hidden lg:table-cell"
                >
                  <div className="flex items-center">
                    Created {sortIcon("createdAt")}
                  </div>
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {data.length > 0 ? (
                data.map((portfolio) => (
                  <tr
                    key={portfolio._id}
                    className={`hover:bg-gray-50 transition-colors ${isNewPortfolio(portfolio.createdAt, markedAsReadPortfolios)
                      ? "bg-blue-50 border-l-4 border-l-blue-400"
                      : ""
                      }`}
                  >
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div className="text-sm font-medium text-gray-900">
                          {portfolio.portfolioName}
                        </div>
                        {/* Blue dot indicator for new portfolios */}
                        {isNewPortfolio(portfolio.createdAt, markedAsReadPortfolios) && (
                          <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0 animate-pulse"></span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500 md:hidden">
                        {portfolio.portfolioType} | {portfolio.riskProfile}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {portfolio.userData?.fullName}
                      </div>
                      <div className="text-xs text-gray-500">
                        {portfolio.userData?.email}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap hidden md:table-cell">
                      <div className="text-sm text-gray-900">
                        {portfolio.portfolioType}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap hidden md:table-cell">
                      <div className="text-sm text-gray-900">
                        {portfolio.riskProfile}
                      </div>
                      <div className="text-xs text-gray-500">
                        {portfolio.investmentStrategy?.replace("_", " ")}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(
                          portfolio.status
                        )}`}
                      >
                        {portfolio.status}
                      </span>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap hidden md:table-cell">
                      <div className="text-sm text-gray-900">
                        ₹
                        {portfolio.summary?.totalInvestment?.toLocaleString() ||
                          "0"}
                      </div>
                      <div className="text-xs text-gray-500">
                        Current: ₹
                        {portfolio.summary?.currentValue?.toLocaleString() ||
                          "0"}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <div
                        className={`text-sm font-medium flex items-center ${getProfitColorClass(
                          portfolio.summary?.totalProfit
                        )}`}
                      >
                        ₹
                        {portfolio.summary?.totalProfit?.toLocaleString() ||
                          "0"}
                        {getProfitIcon(portfolio.summary?.totalProfit)}
                      </div>
                      <div className="text-xs text-gray-500">
                        {portfolio.summary?.profitPercentage
                          ? `${portfolio.summary.profitPercentage.toFixed(2)}%`
                          : "0.00%"}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap hidden lg:table-cell">
                      <div className="flex items-center gap-2">
                        <div className="text-sm text-gray-900">
                          {formatDate(portfolio.createdAt)}
                        </div>
                        {/* Show "NEW" badge for new portfolios */}
                        {isNewPortfolio(portfolio.createdAt, markedAsReadPortfolios) && (
                          <span className="px-2 py-1 text-xs font-semibold bg-blue-100 text-blue-800 rounded-full">
                            NEW
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-gray-500">
                        Updated: {formatDate(portfolio.updatedAt)}
                      </div>
                    </td>
                    <td className="px-3 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleViewDetails(portfolio._id)}
                        className="bg-blue-50 hover:bg-blue-100 text-blue-600 font-medium py-1 px-3 rounded-lg text-sm flex items-center transition-colors"
                      >
                        <Eye className="h-4 w-4 mr-1" />
                        View Details
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan="9"
                    className="px-3 py-8 text-center text-gray-500"
                  >
                    <AlertTriangle className="mx-auto h-8 w-8 text-yellow-500 mb-2" />
                    <p>No portfolios found matching your search criteria.</p>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}



      {/* User-Centric View */}
      {viewMode === "users" && (
        <div className="space-y-6">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <Loader
                  className="animate-spin text-indigo-500 mb-4"
                  size={36}
                />
                <p className="mt-4 text-gray-600">Loading user data...</p>
              </div>
            </div>
          ) : data.length > 0 ? (
            data.map((userData) => (
              <div
                key={userData.user._id}
                className="bg-white rounded-lg border border-gray-200 overflow-hidden"
              >
                {/* User Header */}
                <div className="p-4 bg-gray-50 border-b border-gray-200">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900">
                        {userData.user.fullName}
                      </h3>
                      <p className="text-sm text-gray-600">
                        {userData.user.email}
                      </p>
                    </div>
                    <div className="mt-2 sm:mt-0 flex items-center gap-2">
                      <div className="bg-blue-50 text-blue-700 rounded-lg px-3 py-1 text-sm font-medium">
                        {userData.portfolios.length} Portfolios
                      </div>
                      {/* Show indicator if user has new portfolios */}
                      {userData.portfolios.some(p => isNewPortfolio(p.createdAt)) && (
                        <span className="w-2 h-2 bg-blue-500 rounded-full animate-pulse"></span>
                      )}
                    </div>
                    <div className="mt-4">
                      <FinancialReportsPanel
                        userId={userData.user._id}
                        userFullName={userData?.portfolios.user?.fullName}
                      />
                    </div>
                  </div>

                  {/* User Portfolio Stats */}
                  <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-white p-3 rounded-md border border-gray-200">
                      <p className="text-xs text-gray-500">Total Value</p>
                      <p className="text-lg font-medium">
                        ₹{userData.portfolioStats.totalValue.toLocaleString()}
                      </p>
                    </div>
                    <div className="bg-white p-3 rounded-md border border-gray-200">
                      <p className="text-xs text-gray-500">Average Value</p>
                      <p className="text-lg font-medium">
                        ₹
                        {Math.round(
                          userData.portfolioStats.avgValue
                        ).toLocaleString()}
                      </p>
                    </div>
                    <div className="bg-white p-3 rounded-md border border-gray-200 hidden md:block">
                      <p className="text-xs text-gray-500">User Status</p>
                      <p className="text-lg font-medium capitalize">
                        {userData.user.status || "Active"}
                      </p>
                    </div>
                    <div className="bg-white p-3 rounded-md border border-gray-200 hidden md:block">
                      <p className="text-xs text-gray-500">KYC Status</p>
                      <p className="text-lg font-medium">
                        {userData.user.isKycVerified ? "Verified" : "Pending"}
                      </p>
                    </div>
                  </div>
                </div>
                {/* User's Portfolios */}
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Portfolio Name
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                          Type
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Value
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                          Profit
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {userData.portfolios.map((portfolio) => (
                        <tr key={portfolio._id} className="hover:bg-gray-50">
                          <td className="px-3 py-3 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {portfolio.portfolioName}
                            </div>
                            <div className="text-xs text-gray-500 md:hidden">
                              {portfolio.portfolioType}
                            </div>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap hidden md:table-cell">
                            <div className="text-sm text-gray-900">
                              {portfolio.portfolioType}
                            </div>
                            <div className="text-xs text-gray-500">
                              {portfolio.riskProfile}
                            </div>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap">
                            <span
                              className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(
                                portfolio.status
                              )}`}
                            >
                              {portfolio.status}
                            </span>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              ₹
                              {portfolio.summary?.currentValue?.toLocaleString() ||
                                "0"}
                            </div>
                            <div className="text-xs text-gray-500">
                              Invested: ₹
                              {portfolio.summary?.totalInvestment?.toLocaleString() ||
                                "0"}
                            </div>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap hidden md:table-cell">
                            <div
                              className={`text-sm font-medium flex items-center ${getProfitColorClass(
                                portfolio.summary?.totalProfit
                              )}`}
                            >
                              ₹
                              {portfolio.summary?.totalProfit?.toLocaleString() ||
                                "0"}
                              {getProfitIcon(portfolio.summary?.totalProfit)}
                            </div>
                            <div className="text-xs text-gray-500">
                              {portfolio.summary?.profitPercentage
                                ? `${portfolio.summary.profitPercentage.toFixed(
                                  2
                                )}%`
                                : "0.00%"}
                            </div>
                          </td>
                          <td className="px-3 py-3 whitespace-nowrap">
                            <button
                              onClick={() => handleViewDetails(portfolio._id)}
                              className="bg-blue-50 hover:bg-blue-100 text-blue-600 font-medium py-1 px-3 rounded-lg text-sm flex items-center transition-colors"
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Details
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))
          ) : (
            <div className="bg-white rounded-lg border border-gray-200 p-8 text-center text-gray-500">
              <AlertTriangle className="mx-auto h-8 w-8 text-yellow-500 mb-2" />
              <p>No users found matching your search criteria.</p>
            </div>
          )}
        </div>
      )}
      {viewMode === "Portfolio Report" && (
        <>
          {" "}
          <FinancialReportsPage />
        </>
      )}
      {/* Pagination */}
      {viewMode !== "Portfolio Report" && (
        <div className="mt-4 flex flex-col sm:flex-row justify-between items-center border-t pt-4">
          <p className="text-sm text-gray-600 mb-2 sm:mb-0">
            Showing{" "}
            {pagination.total === 0
              ? 0
              : (pagination.page - 1) * pagination.limit + 1}{" "}
            to {Math.min(pagination.page * pagination.limit, pagination.total)} of{" "}
            {pagination.total} results
          </p>
          <div className="flex gap-2">
            <button
              disabled={pagination.page === 1}
              onClick={() => setCurrentPage(pagination.page - 1)}
              className={`px-3 py-1 border rounded-md ${pagination.page === 1
                ? "text-gray-300 cursor-not-allowed"
                : "text-gray-600 hover:bg-gray-100"
                }`}
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            {Array.from({ length: Math.min(pagination.pages, 3) }, (_, i) => {
              // Show current page and surrounding pages
              let pageNum;
              if (pagination.pages <= 3) {
                pageNum = i + 1;
              } else if (pagination.page === 1) {
                pageNum = i + 1;
              } else if (pagination.page === pagination.pages) {
                pageNum = pagination.pages - 2 + i;
              } else {
                pageNum = pagination.page - 1 + i;
              }
              return (
                <button
                  key={pageNum}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`px-3 py-1 border rounded-md ${pagination.page === pageNum
                    ? "bg-blue-500 text-white"
                    : "text-gray-600 hover:bg-gray-100"
                    }`}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              disabled={
                pagination.page === pagination.pages || pagination.pages === 0
              }
              onClick={() => setCurrentPage(pagination.page + 1)}
              className={`px-3 py-1 border rounded-md ${pagination.page === pagination.pages || pagination.pages === 0
                ? "text-gray-300 cursor-not-allowed"
                : "text-gray-600 hover:bg-gray-100"
                }`}
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
